import useUrlState from '@ahooksjs/use-url-state'
import { FieldTimeOutlined, ImportOutlined, InfoCircleOutlined, SwapOutlined } from '@ant-design/icons'
import { useInViewport } from 'ahooks'
import { Avatar, Button, Menu, Popover, Tooltip, Typography } from 'antd'
import { isNumber } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { AppHeader, ColumnLayout, UserAvatarGroup } from '@/components'
import { useIsFeatureToggleOn } from '@/components/feature-toggle/feature-toggle.hooks'
import { useGetViewerQuery } from '@/generated/graphql'
import { useLayoutSize } from '@/hooks/layout-size.hooks'
import { appState } from '@/modules/app/app.state'
import { ProfileContainer } from '@/modules/user/profile/profile.container'
import { UserSidebar } from '@/modules/user/user-sidebar/user-sidebar.component'
import { UserTimeline } from '@/modules/user/user-timeline/user-timeline.component'

import { useIsSystemAdmin } from '../admin/admin.hooks'
import { UserInfoHeader } from '../user/user-info/user-info-header/user-info-header.component'
import { UserInfoContainer } from '../user/user-info/user-info.container'
import { ArchiveContainer } from './archive/archive.container'
import { FavorContainer } from './archive/favorite/favorite.container'
import s from './me.module.less'

interface State {
  tab: string | undefined
}

export const UserInfoContext = React.createContext<{
  isGlobalEdit: boolean
  setIsGlobalEdit: React.Dispatch<React.SetStateAction<boolean>>
}>({ isGlobalEdit: false, setIsGlobalEdit: () => {} })

export const MeContainer = () => {
  const avatarMaxCount = 10
  const { t } = useTranslation()

  const [state, setState] = useUrlState<State>({
    tab: 'profile',
  })
  const [isUserInfoPageVisible, setIsUserInfoPageVisible] = React.useState(true)
  const [isGlobalEdit, setIsGlobalEdit] = React.useState(false)

  const isImpactFeatureToggleOn = useIsFeatureToggleOn('impact')

  const headerRef = React.useRef<HTMLDivElement>(null)
  const isHeaderInView = useInViewport(() => headerRef.current)

  const isSystemAdmin = useIsSystemAdmin()
  const { isMobileSize } = useLayoutSize()

  const { data, error } = useGetViewerQuery({
    variables: {
      withImpact: true,
      withPageVisitors: true,
      withYesterdayPageVisitors: true,
      pageVisitorPagination: {
        last: avatarMaxCount,
      },
      yesterdayPageVisitorPagination: {
        last: avatarMaxCount,
      },
    },
  })

  const pageSwitch = state.tab === 'profile' && isSystemAdmin && (
    <Button
      type='primary'
      ghost
      style={{ marginRight: 8, display: 'none' }}
      icon={<SwapOutlined />}
      onClick={() => setIsUserInfoPageVisible((value) => !value)}
    />
  )

  React.useEffect(() => {
    appState({ ...appState(), showSidebar: false })
    return () => {
      appState({ ...appState(), showSidebar: true })
    }
  }, [])

  if (error || !data) {
    return null
  }

  const me = data.viewer

  const viewedCount = me?.viewedUsers!.totalCount || 0
  const viewedUsers = me?.viewedUsers!.edges.map(({ node }) => node).reverse() || []

  const yesterdayViewedCount = me.yesterdayVisitors!.totalCount || 0
  const yesterdayViewedUsers = me?.yesterdayVisitors!.edges.map(({ node }) => node) || []

  const yesterdayViewed = !isMobileSize && (
    <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
      <Tooltip title={t('pageVisit.yesterday')}>
        <ImportOutlined style={{ fontSize: 18, color: '#4875bb', marginRight: 8, cursor: 'pointer' }} />
      </Tooltip>
      {yesterdayViewedCount > 0 ? (
        <UserAvatarGroup
          users={yesterdayViewedUsers}
          totalCount={yesterdayViewedCount}
          showCount={avatarMaxCount}
          userId={me.userId}
          title={t('pageVisit.yesterday')}
          type='yesterdayVisitors'
          showSearch={false}
          avatarTooltipMessage={t('user.avatar.group.others', { count: yesterdayViewedCount - avatarMaxCount + 1 })}
        />
      ) : (
        t('pageVisit.noVisitor')
      )}
    </div>
  )

  const viewed = !isMobileSize && (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={t('pageVisit.history')}>
        <FieldTimeOutlined style={{ fontSize: 18, color: '#48bbb5', marginRight: 8, cursor: 'pointer' }} />
      </Tooltip>
      {viewedCount > 0 ? (
        <UserAvatarGroup
          users={viewedUsers}
          totalCount={viewedCount}
          showCount={avatarMaxCount}
          userId={me.userId}
          title={t('pageVisit.history')}
          type='userPageVisitors'
          showSearch={false}
          avatarTooltipMessage={t('user.avatar.group.others', { count: viewedCount - avatarMaxCount + 1 })}
        />
      ) : (
        t('pageVisit.noVisitor')
      )}
    </div>
  )

  const impacts = isImpactFeatureToggleOn && (
    <div className={s.box}>
      <div className={s.info}>
        <Popover
          placement='rightBottom'
          title={
            <Typography.Title level={5} style={{ marginBottom: 0, padding: '8px 0' }}>
              影响力系统使用指南
            </Typography.Title>
          }
          getPopupContainer={(props) => props}
          content={
            <div className={s.handbook}>
              <span>💡 什么是影响力</span>
              <ul>
                <li>{`根据您在本平台的内容贡献和活跃程度综合计算得出`}</li>
              </ul>
              <span>💡 如何提升影响力</span>
              <ul>
                <li>{`点赞、评论、发帖等都能提升影响力`}</li>
                <li>{`还有很多提升影响力的方法，请尽情探索`}</li>
              </ul>
              <span>💡 影响力有什么用</span>
              <ul style={{ marginBottom: 0 }}>
                <li style={{ textDecorationLine: 'line-through' }}>{`比别人高就很爽`}</li>
                <li>{`您可以直观感受到自己在 Tale 这个平台的成长和贡献`}</li>
                <li>{`积分商城不久后就会与大家见面，敬请期待`}</li>
              </ul>
            </div>
          }
        >
          <InfoCircleOutlined className={s.infoIcon} />
        </Popover>
      </div>
      <div className={s.display}>
        <div>
          {t('impact.score.yesterdayContribute')}
          <span style={{ color: '#DD403A', marginLeft: 4 }}>
            {isNumber(me.contributedImpact) ? Math.round(me.contributedImpact).toLocaleString() : 0}
          </span>
        </div>
        <div>
          {t('impact.score.yesterdayImpact')}
          <span style={{ color: '#FFA10A', marginLeft: 4 }}>
            {isNumber(me.yesterdayImpact) ? Math.round(me.yesterdayImpact).toLocaleString() : 0}
          </span>
        </div>
        <div>
          {t('impact.score.currentImpact')}
          <span style={{ color: '#20A39E', marginLeft: 4 }}>
            {isNumber(me.impact) ? Math.round(me.impact).toLocaleString() : 0}
          </span>
        </div>
      </div>
    </div>
  )

  const visitors = (
    <div className={s.visitorDisplay}>
      <div>{viewed}</div>
      <div>{yesterdayViewed}</div>
    </div>
  )

  const edit = state.tab === 'profile' && isSystemAdmin && (
    <div className={s.edit} style={{ display: 'none' }}>
      {
        <Button type='primary' onClick={() => setIsGlobalEdit(true)}>
          {t('user.info.edit')}
        </Button>
      }
    </div>
  )

  const userHeaderMenu = (
    <Menu className={s.menu} mode='horizontal' selectedKeys={[state.tab]} onClick={({ key: tab }) => setState({ tab })}>
      <Menu.Item style={{ paddingLeft: 0 }} key='profile'>
        {t('personalMenu.profile')}
      </Menu.Item>
      <Menu.Item key='myposts'>{t('personalMenu.mytimeline')}</Menu.Item>
      <Menu.Item key='myFavor'>{t('personalMenu.myFavor')}</Menu.Item>
      <Menu.Item key='myArchive'>{t('personalMenu.myArchive')}</Menu.Item>
    </Menu>
  )

  const menu = (
    <Menu mode='horizontal' selectedKeys={[state.tab]} onClick={({ key: tab }) => setState({ tab })}>
      <Menu.Item key='myposts'>{t('personalMenu.mytimeline')}</Menu.Item>
      <Menu.Item key='profile'>{t('personalMenu.profile')}</Menu.Item>
      <Menu.Item key='myFavor'>{t('personalMenu.myFavor')}</Menu.Item>
      <Menu.Item key='myArchive'>{t('personalMenu.myArchive')}</Menu.Item>
    </Menu>
  )

  const name = me.name + (me.alias ? ` (${me.alias})` : '')

  const header = (
    <>
      <div ref={headerRef}>
        <AppHeader
          title={!isUserInfoPageVisible && name}
          titleString={name}
          avatar={!isUserInfoPageVisible && <Avatar size='large' src={me.avatarUrl} />}
          userHeader={
            isUserInfoPageVisible && (
              <UserInfoHeader userId={me.userId} readonly={false}>
                {userHeaderMenu}
              </UserInfoHeader>
            )
          }
          actions={
            <div className={s.actions}>
              <div className={s.buttons}>
                {pageSwitch}
                {edit}
                {impacts}
              </div>
              <div className={s.actionContainer}>{visitors}</div>
            </div>
          }
          center={isUserInfoPageVisible}
        >
          {!isUserInfoPageVisible && menu}
        </AppHeader>
      </div>
      {!isHeaderInView && (
        <AppHeader
          isFixed={!isHeaderInView}
          title={name}
          titleString={name}
          avatar={<Avatar size='large' src={me.avatarUrl} />}
          center={isUserInfoPageVisible}
        />
      )}
    </>
  )

  const middle = (() => {
    switch (state.tab) {
      case 'myposts':
        return <UserTimeline userId={me.userId} />
      case 'profile':
        return isUserInfoPageVisible ? (
          <UserInfoContext.Provider value={{ isGlobalEdit, setIsGlobalEdit }}>
            <UserInfoContainer userId={me.userId} />
          </UserInfoContext.Provider>
        ) : (
          <ProfileContainer userId={me.userId} />
        )
      case 'myFavor':
        return <FavorContainer />
      case 'myArchive':
        return <ArchiveContainer />
    }
  })()

  const right = state.tab === 'myposts' ? <UserSidebar userId={me.userId} /> : null

  return <ColumnLayout stickyHeader={false} header={header} middle={middle} right={right} />
}
